import { CloseIcon } from '@diligentcorp/atlas-react-icons';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { LoadingButton, LoadingButtonProps } from '../buttons/loading-button';
import { GeneralErrorUI } from '../errors/generalError';

type ExtendedButtonProps = LoadingButtonProps & {
  text: string;
};
export interface ConfirmationModalProps {
  onAccept: () => void;
  onCancel: () => void;
  isOpen: boolean;
  title: ReactNode;
  description: ReactNode;
  acceptButtonProps: ExtendedButtonProps;
  cancelButtonProps: ExtendedButtonProps;
  swapButtons?: boolean;
  error?: boolean;
}

export function ConfirmationModal({
  isOpen,
  onAccept,
  onCancel,
  title,
  description,
  acceptButtonProps,
  cancelButtonProps,
  swapButtons = false,
  error = false,
}: ConfirmationModalProps) {
  const Buttons = useMemo(() => {
    const Accept = (
      <LoadingButton
        variant='contained'
        data-testid='accept-button'
        onClick={onAccept}
        {...acceptButtonProps}
      >
        {acceptButtonProps.text}
      </LoadingButton>
    );
    const Cancel = (
      <Button
        variant='outlined'
        data-testid='cancel-button'
        onClick={onCancel}
        {...cancelButtonProps}
      >
        {cancelButtonProps.text}
      </Button>
    );
    if (swapButtons) {
      return (
        <>
          {Cancel}
          {Accept}
        </>
      );
    }
    return (
      <>
        {Accept}
        {Cancel}
      </>
    );
  }, [swapButtons, acceptButtonProps, cancelButtonProps]);

  return (
    <Dialog
      open={isOpen}
      aria-describedby='confirmation-modal-content'
      aria-labelledby='confirmation-modal-title'
      data-testid='confirmation-modal'
      onClose={error ? onCancel : undefined}
    >
      {error ? (
        <GeneralErrorUI />
      ) : (
        <>
          <DialogTitle>
            {title}
            <IconButton
              onClick={onCancel}
              aria-label='confirmation-modal-close'
              color='inherit'
              data-testid='close-button'
              disabled={cancelButtonProps.disabled}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent data-testid='confirmation-modal-content'>
            <DialogContentText display='flex' flexDirection='column' rowGap={2}>
              {description}
            </DialogContentText>
          </DialogContent>
          <DialogActions>{Buttons}</DialogActions>
        </>
      )}
    </Dialog>
  );
}
